import { Spin } from 'antd';
import { useFormik } from 'formik';
import produce from 'immer';
import get from 'lodash/get';
import { observer } from 'mobx-react';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import { Button } from '../../components/button';
import { CmsUserHistoryBar } from '../../components/cms-user-history-bar';
import { DeleteButton, SaveButton } from '../../components/action-buttons';
import { LabeledField } from '../../components/form-fields/labeled-field';
import { NotificationManager } from '../../components/notification-manager';
import { PageTopBar } from '../../components/page-top-bar';
import { Section } from '../../components/form-fields/section';
import { TimezoneSelect } from '../../components/timezone-select';
import {
    useAuthStore,
    useCategoryStore,
    useOfferStore,
    usePromotionGrantStore,
    usePromotionStore,
} from '../../contexts/mobx';
import { useUnsavedChanges } from '../../contexts/unsaved-changes';
import {
    Promotion,
    PromotionForm,
    YupPromotionForm,
} from '../../stores/promotion';
import { Grant } from '../../stores/promotion-grant';
import { PaginatedList } from '../../types';
import style from './style.module.scss';

import { InfoBanner } from '../../components/info-banner';
import { CsvContent } from '../../types';
import { TEN_PER_PAGE } from '../../utils/const';
import { AcvtSupportedLanguages } from '../../types/acvt';
import type { PreviewTabKey } from '../../components/preview';
import { downloadCSVFile } from '../../utils/csv';
import { formatAppTargets } from '../../utils/app-target';
import {
    createPromotion,
    onDeleteConfirm,
    setCategory,
    updatePromotion,
    convertAvailabilityFromApi,
} from '../../utils/promotion';
import {
    convertUtcToLocalMoment,
    getUserTimezoneOrFallback,
} from '../../utils/timezone';
import { useNavigation } from '../../utils/use-navigation';
import { usePlacementLinking } from '../../utils/use-placement-linking';
import { ABTestingSection } from './components/ABTestingSection';
import { AppPreviewSection } from './components/AppPreviewSection';
import AvailabilitiesSection from './components/AvailabilitiesSection';
import { ConfigSection } from './components/ConfigSection';
import { Brand, DeepLinkSection } from '../../components/deep-link-section';
import { useDeepLinkVisibility } from '../../components/deep-link-section/use-deep-link-visibility';
import type { DeepLink } from '../../components/deep-link-section';
import { OfferDetailsSection } from './components/OfferDetailsSection';
import { ScheduleSection } from './components/ScheduleSection';
import { StatsSection } from './components/StatsSection';
import { StatusSection } from './components/StatusSection';
import { WinnableSection } from './components/WinnableSection';
import {
    getPromotionsTypeExistingTitle,
    getPromotionsTypeNewTitle,
} from '../../utils/promotion-types';
import type { PromotionTypes } from '../../utils/const';

const YupParams = yup.object({
    action: yup.string().oneOf(['create', 'edit', 'duplicate']).required(),
    id: yup
        .string()
        .test('shouldBeDefined', 'Id should be defined', function (v) {
            const action = this.resolve(yup.ref('action'));
            return !(v === undefined && action !== 'create');
        }),
});

type Params = yup.InferType<typeof YupParams>;
// Convert from API format to Form format (for fetching/display)
const converter = (promo: Promotion): PromotionForm => {
    const timezone = promo.timezone || getUserTimezoneOrFallback();

    return {
        ...promo,
        // Convert UTC dates from database TO the timezone for display
        startDate: convertUtcToLocalMoment(promo.startDate, promo.timezone),
        endDate: convertUtcToLocalMoment(promo.endDate, promo.timezone),
        activationReadyDate: convertUtcToLocalMoment(
            promo.activationReadyDate,
            promo.timezone
        ),
        deactivationDisplayDate: convertUtcToLocalMoment(
            promo.deactivationDisplayDate,
            promo.timezone
        ),
        items: promo.items.map((p) => ({
            ...p,
            description: p.description ?? '',
        })),
        // Keep relation IDs for existing province assignments
        provinces: (promo.promotionProvinces || []).map((p) => ({
            businessUnitId: p.id, // Existing relation ID
            provinceId: p.province.id,
        })),
        // Convert availabilities from UTC to local timezone
        availabilities: promo.availabilities
            ? promo.availabilities.map((avail) =>
                  convertAvailabilityFromApi(
                      {
                          ...avail,
                          startDate: moment(avail.startDate).toISOString(),
                          endDate: moment(avail.endDate).toISOString(),
                      },
                      timezone
                  )
              )
            : undefined,
    };
};

const sanitizeForDuplicate = produce((promotion: PromotionForm) => {
    delete promotion.startDate;
    delete promotion.endDate;
    delete promotion.ctDeepLink;
    delete promotion.ckDeepLink;
});

export const CouponPage = observer(
    (props: { promotionType?: PromotionTypes }) => {
        const [isFetching, setIsFetching] = useState<boolean>(false);
        const [isDeleting, setIsDeleting] = useState<boolean>(false);
        const [isGrantsLoading, setIsGrantsLoading] = useState<boolean>(false);
        const store = usePromotionGrantStore();
        const categoryStore = useCategoryStore();
        const promotionStore = usePromotionStore();
        const offerStore = useOfferStore();
        const params = useParams<Params>();
        const navigation = useNavigation();
        const { redirectAfterCreate } = usePlacementLinking();
        const [grantsData, setGrantsData] = useState<PaginatedList<
            Grant
        > | null>(null);
        const authStore = useAuthStore();
        const hasFetchedPromotion = useRef(false);
        const { hasManagementAccess } = authStore;

        useEffect(() => {
            if (!hasManagementAccess) {
                navigation.goToHome();
            }
        }, [hasManagementAccess, navigation]);

        useEffect(() => {
            if (!YupParams.isValidSync(params)) {
                props.promotionType === 'coupon'
                    ? navigation.replaceToCouponCreate()
                    : navigation.replaceToInStoreCouponCreate();
            }
        }, [params, navigation]);

        const formik = useFormik<PromotionForm>({
            initialValues: {
                startDate: moment().startOf('day'),
                endDate: moment().endOf('day'),
                timezone: getUserTimezoneOrFallback(),
                activationReadyDate: null,
                deactivationDisplayDate: null,
                id: 0,
                isForMajor: false,
                isForCTConnect: false,
                isForCarwash: false,
                isKillerPromo: false,
                isForRoadToRewards: false,
                isForFuelTester: false,
                isActive: true,
                isVisibleOnHomePage: true,
                isPrivate: false,
                isPromotedBanner: false,
                activationCode: '',
                quantity: 0,
                type: props.promotionType,
                category: undefined,
                categoryId: undefined,
                promotionalOffer: null,
                promotionalOfferId: null,
                canBeDeleted: false,
                discountValue: 0,
                originalPrice: 0,
                rebateAmount: 0,
                totalActivations: 0,
                totalGrants: 0,
                hasPreview: false,
                items: [
                    {
                        title: '',
                        subTitle: '',
                        description: '',
                        thumbnail: '',
                        backgroundColor: null,
                        promotedBannerTextColor: null,
                        outsideActivatableHours: '',
                        language: 'en',
                        previewImageUrl: '',
                        previewTitle: '',
                    },
                    {
                        title: '',
                        subTitle: '',
                        description: '',
                        thumbnail: '',
                        backgroundColor: null,
                        promotedBannerTextColor: null,
                        outsideActivatableHours: '',
                        language: 'fr',
                        previewImageUrl: '',
                        previewTitle: '',
                    },
                ],
                cmsUserHistory: [],
                inTestingCampaign: false,
                autoReissueEnabled: false,
                provinces: [],
            },
            validateOnMount: true,
            validationSchema: YupPromotionForm,
            onSubmit: (values) => {
                alert(JSON.stringify(values, null, 2));
            },
        });

        useEffect(() => {
            categoryStore.fetchActiveCategories();
        }, [categoryStore]);

        useEffect(() => {
            offerStore.fetchOffers();
        }, [offerStore]);

        const isDuplicate = params.action === 'duplicate';

        useEffect(() => {
            if (
                (params.action === 'edit' || isDuplicate) &&
                !hasFetchedPromotion.current
            ) {
                hasFetchedPromotion.current = true;
                setIsFetching(true);
                promotionStore
                    .fetchSinglePromotion(Number(params.id!))
                    .then((promotion) => {
                        const modifiedPromotion = isDuplicate
                            ? sanitizeForDuplicate(converter(promotion))
                            : converter(promotion);
                        // Use resetForm to set loaded data as the new "initial" state
                        // This ensures dirty is false after loading (except for duplicates)
                        if (isDuplicate) {
                            formik.setValues(modifiedPromotion);
                        } else {
                            formik.resetForm({ values: modifiedPromotion });
                        }
                    })
                    .catch(() => {
                        hasFetchedPromotion.current = false;
                        props.promotionType === 'coupon'
                            ? navigation.replaceToCouponCreate()
                            : navigation.replaceToInStoreCouponCreate();
                    })
                    .finally(() => setIsFetching(false));
            }
        }, [
            params.action,
            params.id,
            promotionStore,
            navigation,
            isDuplicate,
            formik,
        ]);

        const checkIfHasError = (path: string) => {
            const hasError = Boolean(get(formik.errors, path));
            const isTouched = Boolean(get(formik.touched, path));
            return hasError && (isTouched || params.action !== 'create');
        };

        const clearDirtyRef = useRef<(() => void) | null>(null);

        const handleCreatePromotion = useCallback(async () => {
            if (formik.values.hasPreview) {
                const missingPreviewImages = formik.values.items.some(
                    (item) => !item.previewImageUrl
                );

                if (missingPreviewImages) {
                    NotificationManager.showError(
                        'Preview mode is enabled but some languages are missing a preview image'
                    );
                    return;
                }
            }

            const hasBothBackgroundTypes = formik.values.items.some(
                (item) => item.backgroundColor && item.background
            );

            if (hasBothBackgroundTypes) {
                NotificationManager.showError(
                    'Cannot have both background color and background image. Please remove one before saving.'
                );
                return;
            }

            await createPromotion(
                formik,
                promotionStore,
                navigation,
                authStore,
                clearDirtyRef.current ?? undefined,
                {
                    onRedirect: () =>
                        redirectAfterCreate(() =>
                            formik.values.type === 'coupon'
                                ? navigation.replaceToCoupons()
                                : navigation.replaceToCouponsInStore()
                        ),
                }
            );
        }, [
            authStore,
            formik,
            navigation,
            promotionStore,
            redirectAfterCreate,
        ]);

        const handleUpdatePromotion = useCallback(async () => {
            if (formik.values.hasPreview) {
                const missingPreviewImages = formik.values.items.some(
                    (item) => !item.previewImageUrl
                );

                if (missingPreviewImages) {
                    NotificationManager.showError(
                        'Preview mode is enabled but some languages are missing a preview image'
                    );
                    return;
                }
            }

            const hasBothBackgroundTypes = formik.values.items.some(
                (item) => item.backgroundColor && item.background
            );

            if (hasBothBackgroundTypes) {
                NotificationManager.showError(
                    'Cannot have both background color and background image. Please remove one before saving.'
                );

                return;
            }

            await updatePromotion(
                formik,
                promotionStore,
                navigation,
                authStore,
                clearDirtyRef.current ?? undefined
            );
        }, [authStore, formik, navigation, promotionStore]);

        // Save handler for unsaved changes modal
        const handleSaveFromModal = useCallback(async () => {
            if (params.action === 'edit') {
                await handleUpdatePromotion();
            } else {
                await handleCreatePromotion();
            }
        }, [params.action, handleUpdatePromotion, handleCreatePromotion]);

        const { clearDirty } = useUnsavedChanges({
            dirty: formik.dirty,
            onSave: handleSaveFromModal,
        });

        clearDirtyRef.current = clearDirty;

        const handleSetCategory = (categoryId: number) => {
            setCategory(categoryId, formik, categoryStore);
        };

        const handleDeleteConfirm = async () => {
            if (!formik.values.type) {
                console.error('Promotion type is undefined');
                return;
            }
            if (params.id) {
                await onDeleteConfirm(
                    { id: params.id, type: formik.values.type },
                    setIsDeleting,
                    promotionStore,
                    navigation
                );
            } else {
                console.error('Promotion ID is undefined');
            }
        };

        const promotionId =
            formik.values.id && params.action !== 'duplicate'
                ? formik.values.id
                : 0;

        const fetchGrantsCallback = useCallback(
            async (page: number, perPage: number) => {
                if (!promotionId) return;
                setIsGrantsLoading(true);
                try {
                    const data = await store.fetchGrantsPaginated(
                        promotionId,
                        page + 1,
                        perPage
                    );
                    setGrantsData(data);
                } catch (error) {
                    console.error(error);
                } finally {
                    setIsGrantsLoading(false);
                }
            },
            [store, promotionId]
        );

        useEffect(() => {
            if (params.action !== 'create' && promotionId) {
                fetchGrantsCallback(0, TEN_PER_PAGE);
            }
        }, [params.action, promotionId, fetchGrantsCallback]);

        const downloadWinners = useCallback(async () => {
            if (!promotionId) return;
            try {
                const allGrants = await store.fetchAllGrants(promotionId);
                const flattenGrants: CsvContent = allGrants.map((grant) => ({
                    'Client ID': grant.clientId,
                    Email: grant.client.email ?? '',
                    'Full Name': grant.client.fullName ?? '',
                    App: formatAppTargets(grant.client.appTargets),
                    Language: grant.client.language ?? '',
                    Count: grant.cnt,
                }));
                return downloadCSVFile(flattenGrants, 'winners');
            } catch (error) {
                console.error(error);
                NotificationManager.showError('Error downloading winners list');
            }
        }, [store, promotionId]);

        const [lang, setLang] = useState<AcvtSupportedLanguages>('en');
        const [selectedTab, setSelectedTab] = useState<PreviewTabKey>(
            'products'
        );

        const { showCoucheTard, showCircleK } = useDeepLinkVisibility(
            formik.values.provinces
        );

        const deepLinks: DeepLink[] = [];
        if (showCoucheTard) {
            deepLinks.push({
                brand: Brand.CoucheTard,
                url: formik.values.ctDeepLink ?? null,
            });
        }
        if (showCircleK) {
            deepLinks.push({
                brand: Brand.CircleK,
                url: formik.values.ckDeepLink ?? null,
            });
        }

        const transformId =
            !isDuplicate && params.id ? Number(params.id) : undefined;

        const renderTopBarActions = () => {
            switch (params.action) {
                case 'create':
                    return (
                        <SaveButton
                            onClick={handleCreatePromotion}
                            loading={promotionStore.isUpsertingPromotion}
                            disabled={!formik.isValid}
                        />
                    );
                case 'edit':
                    return (
                        <>
                            <DeleteButton
                                onClick={handleDeleteConfirm}
                                loading={isDeleting}
                            />
                            {/* Duplicate button */}
                            <Button
                                variant="secondary"
                                onClick={() =>
                                    props.promotionType === 'coupon'
                                        ? navigation.goToCouponDuplicate(
                                              promotionId
                                          )
                                        : navigation.goToInStoreCouponDuplicate(
                                              promotionId
                                          )
                                }
                                loading={promotionStore.isUpsertingPromotion}
                            >
                                Duplicate
                            </Button>
                            <SaveButton
                                onClick={handleUpdatePromotion}
                                loading={promotionStore.isUpsertingPromotion}
                                disabled={!formik.isValid}
                            />
                        </>
                    );
                case 'duplicate':
                    return (
                        <SaveButton
                            onClick={handleCreatePromotion}
                            loading={promotionStore.isUpsertingPromotion}
                            disabled={!formik.isValid}
                        />
                    );
                default:
                    return null;
            }
        };

        return (
            <>
                <PageTopBar
                    title={
                        params.action === 'edit' && transformId
                            ? `${getPromotionsTypeExistingTitle(
                                  formik.values.type
                              )}${transformId}`
                            : getPromotionsTypeNewTitle(props.promotionType)
                    }
                    goBack={{
                        callback: () =>
                            props.promotionType === 'coupon'
                                ? navigation.goToCoupons()
                                : navigation.goToCouponsInStore(),
                        text:
                            params.action === 'duplicate'
                                ? 'Back to promotion'
                                : 'Back to list',
                    }}
                >
                    {renderTopBarActions()}
                </PageTopBar>

                <div className="content_with_margins">
                    <form
                        className={
                            isFetching
                                ? style.promotion_form_hidden
                                : style.promotion_form
                        }
                    >
                        <div className={style.promotion_page_content}>
                            <div className={style.left_side}>
                                {isDuplicate && (
                                    <InfoBanner
                                        message={`You are duplicating the coupon #${params.id}`}
                                        description="Any changes you make will create a new coupon and will not affect the original coupon."
                                    />
                                )}
                                {isFetching && <Spin />}

                                <ConfigSection
                                    values={formik.values}
                                    setFieldValue={formik.setFieldValue}
                                    handleChange={formik.handleChange}
                                    categoryStore={{
                                        isFetchingActiveCategories:
                                            categoryStore.isFetchingActiveCategories,
                                        activeCategories:
                                            categoryStore.activeCategories,
                                    }}
                                    offerStore={offerStore}
                                    setCategory={handleSetCategory}
                                />

                                <OfferDetailsSection
                                    values={formik.values as PromotionForm}
                                    setFieldValue={formik.setFieldValue}
                                    offerStore={{
                                        offers: offerStore.offers,
                                        isFetchingOffers:
                                            offerStore.isFetchingOffers,
                                        getOffersById: offerStore.getOffersById.bind(
                                            offerStore
                                        ),
                                        fetchOffers: offerStore.fetchOffers.bind(
                                            offerStore
                                        ),
                                        offersMap: offerStore.offersMap,
                                    }}
                                    handleChange={formik.handleChange}
                                    handleBlur={formik.handleBlur}
                                />

                                {/* Timezone section */}
                                <Section title="Timezone">
                                    <LabeledField helperText="The timezone for all dates, times, and availability schedules in this coupon page.">
                                        <TimezoneSelect
                                            value={formik.values.timezone}
                                            onChange={(value) =>
                                                formik.setFieldValue(
                                                    'timezone',
                                                    value
                                                )
                                            }
                                            style={{ width: 320 }}
                                        />
                                    </LabeledField>
                                </Section>

                                <ScheduleSection
                                    values={formik.values}
                                    setFieldValue={formik.setFieldValue}
                                    checkIfHasError={checkIfHasError}
                                />

                                <AvailabilitiesSection
                                    values={formik.values}
                                    setFieldValue={formik.setFieldValue}
                                    checkIfHasError={checkIfHasError}
                                />

                                <ABTestingSection
                                    inTestingCampaign={
                                        formik.values.inTestingCampaign
                                    }
                                    originalPrice={
                                        formik.values.originalPrice ?? 0
                                    }
                                    rebateAmount={
                                        formik.values.rebateAmount ?? 0
                                    }
                                    setFieldValue={formik.setFieldValue}
                                />

                                <WinnableSection
                                    isPrivate={formik.values.isPrivate}
                                    setFieldValue={formik.setFieldValue}
                                    fetchGrants={fetchGrantsCallback}
                                    grantsData={grantsData}
                                    isFetching={isGrantsLoading}
                                    downloadWinners={downloadWinners}
                                />
                            </div>

                            <div className={style.right_side}>
                                <StatusSection
                                    values={formik.values}
                                    setFieldValue={formik.setFieldValue}
                                    isLoading={
                                        promotionStore.isUpsertingPromotion
                                    }
                                />
                                <AppPreviewSection
                                    values={formik.values}
                                    language={lang}
                                    onLanguageChange={setLang}
                                    selectedTab={selectedTab}
                                    onTabChange={setSelectedTab}
                                />
                                <DeepLinkSection links={deepLinks} />
                                <StatsSection
                                    totalGrants={formik.values.totalGrants}
                                    totalActivations={
                                        formik.values.totalActivations
                                    }
                                    discountValue={formik.values.discountValue}
                                    isPrivate={formik.values.isPrivate}
                                />
                            </div>
                        </div>

                        {formik.values.cmsUserHistory &&
                            formik.values.cmsUserHistory.length > 0 &&
                            !isDuplicate && (
                                <div className={style.cms_history_wrapper}>
                                    <CmsUserHistoryBar
                                        cmsUserHistory={
                                            formik.values.cmsUserHistory
                                        }
                                    />
                                </div>
                            )}
                    </form>
                </div>
            </>
        );
    }
);
